// src/api/clientes.js - API para manejo de clientes (CORREGIDO PARA API REAL)
import { apiFetch, API_CONFIG } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';

// ==================== CONSTANTES ====================

// Tipos de documento permitidos (Venezuela)
export const TIPOS_DOCUMENTO = ['cedula', 'rif', 'pasaporte', 'identificacion_extranjera'];

// Prefijos de teléfono venezolanos permitidos
export const PREFIJOS_TELEFONO = [
  '0412', '0414', '0416', '0424', '0426',
  '0212', // Caracas
  '0241', // Valencia
  '0234', // Punto Fijo
  '0235', // Puerto Cabello
  '0236', // San Felipe
  '0237', // Barquisimeto
  '0238', // Acarigua
  '0239', // Guanare
  '0242', // Maracaibo
  '0243', // Cabimas
  '0244', // Ciudad Ojeda
  '0245', // Punto Fijo (alternativo)
  '0246', // Puerto Ordaz
  '0247', // Ciudad Guayana
  '0248', // El Tigre
  '0249', // Maturín
  '0251', // Barinas
  '0252', // San Cristóbal
  '0253', // Mérida
  '0254', // Valera
  '0255', // San Antonio
  '0256', // Carora
  '0257', // El Vigía
  '0271', // Coro
  '0272', // Punto Fijo
  '0273', // Maracaibo (alternativo)
  '0274', // Valera (alternativo)
  '0275', // Barquisimeto (alternativo)
  '0276', // Acarigua (alternativo)
  '0277', // Guanare (alternativo)
  '0278', // San Felipe (alternativo)
  '0279', // Puerto Cabello (alternativo)
  '0281', // Cumaná
  '0282', // Carúpano
  '0283', // Maturín (alternativo)
  '0284', // Puerto La Cruz
  '0285', // Barcelona
  '0286', // El Tigre (alternativo)
  '0287', // Ciudad Bolívar
  '0288', // Ciudad Guayana (alternativo)
  '0289', // Puerto Ordaz (alternativo)
  '0291', // Maracaibo (alternativo)
  '0292', // Punto Fijo (alternativo)
  '0293', // Coro (alternativo)
  '0294', // Valera (alternativo)
  '0295'  // Barquisimeto (alternativo)
];

// ==================== FUNCIONES AUXILIARES (NO API) ====================

/**
 * Convertir de snake_case (API) a camelCase (frontend)
 * @param {Object} apiCliente - Cliente desde la API
 * @returns {Object} Cliente convertido
 */
const convertirDesdeAPI = (apiCliente) => {
  if (!apiCliente) return null;
  
  // Si viene en un wrapper { data: { ... } } o { cliente: { ... } }
  // pero NO si data es un array (eso lo maneja convertirListaDesdeAPI)
  let source = apiCliente;
  if (apiCliente.data && !Array.isArray(apiCliente.data) && typeof apiCliente.data === 'object') {
    source = apiCliente.data;
  } else if (apiCliente.cliente && typeof apiCliente.cliente === 'object') {
    source = apiCliente.cliente;
  }
  
  // Función auxiliar para buscar propiedades de forma insensible a mayúsculas/minúsculas y guiones
  const getProp = (obj, propName) => {
    if (!obj) return undefined;
    const keys = Object.keys(obj);
    const normalizedProp = propName.toLowerCase().replace(/_/g, '');
    const foundKey = keys.find(k => k.toLowerCase().replace(/_/g, '') === normalizedProp);
    return foundKey ? obj[foundKey] : undefined;
  };

  // Buscar el ID en el source o en el objeto original como fallback
  const id = source.idCliente || source.id || source.id_cliente || 
             source.IdCliente || source.Id || source.Id_Cliente ||
             getProp(source, 'idCliente') || getProp(source, 'id') ||
             apiCliente.idCliente || apiCliente.id || apiCliente.id_cliente ||
             getProp(apiCliente, 'idCliente') || getProp(apiCliente, 'id');

  return {
    // Normalizar campos principales al top-level
    idCliente: id,
    tipoDocumento: source.tipoDocumento || source.tipo_documento || source.TipoDocumento || getProp(source, 'tipoDocumento'),
    documento: source.documento || source.Documento || getProp(source, 'documento'),
    nombre: toTitleCase(source.nombre || source.Nombre || getProp(source, 'nombre') || ''),
    apellido: toTitleCase(source.apellido || source.Apellido || getProp(source, 'apellido') || ''),
    direccion: source.direccion || source.Direccion || getProp(source, 'direccion'),
    telefono: source.telefono || source.Telefono || getProp(source, 'telefono'),
    email: source.email || source.Email || getProp(source, 'email'),
    estado: source.estado || source.Estado || getProp(source, 'estado'),
    fechaCreacion: source.fechaCreacion || source.fecha_creacion || source.FechaCreacion || getProp(source, 'fechaCreacion'),
    
    // Mantener todos los campos originales (incluyendo data si venía envuelto)
    ...apiCliente,
    // Sobrepasar con los campos del source real
    ...source,
    
    // Asegurar que los campos normalizados ganen siempre
    idCliente: id,
    id_cliente: id // Por compatibilidad
  };
};

/**
 * Convertir de camelCase (frontend) a snake_case (API)
 * @param {Object} frontendCliente - Cliente desde el frontend
 * @returns {Object} Cliente para la API
 */
const convertirParaAPI = (frontendCliente) => {
  return {
    tipo_documento: frontendCliente.tipoDocumento,
    documento: frontendCliente.documento,
    nombre: toTitleCase(frontendCliente.nombre),
    apellido: toTitleCase(frontendCliente.apellido || ''),
    direccion: frontendCliente.direccion || '',
    telefono: frontendCliente.telefono || '',
    email: frontendCliente.email || '',
    estado: frontendCliente.estado || 'activo'
  };
};

/**
 * Convertir array de clientes desde API
 * @param {Array} apiClientes - Array de clientes desde API
 * @returns {Array} Array de clientes convertidos
 */
const convertirListaDesdeAPI = (apiClientes) => {
  // Si viene en un wrapper { data: [...] }
  let lista = apiClientes;
  if (apiClientes && apiClientes.data && Array.isArray(apiClientes.data)) {
    lista = apiClientes.data;
  } else if (apiClientes && apiClientes.clientes && Array.isArray(apiClientes.clientes)) {
    lista = apiClientes.clientes;
  }

  if (!Array.isArray(lista)) return [];
  return lista.map(convertirDesdeAPI);
};

/**
 * Formatear fecha de cliente
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora completa
 * @returns {string} Fecha formateada
 */
export const formatearFechaCliente = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Convertir estado a texto legible
 * @param {string} estado - Estado del cliente
 * @returns {string} Estado formateado
 */
export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1);
};

/**
 * Convertir estado a booleano
 * @param {string} estado - Estado como string
 * @returns {boolean} Estado booleano
 */
export const convertirEstadoABoolean = (estado) => {
  return estado?.toLowerCase() === 'activo';
};

/**
 * Formatear tipo de documento (Venezuela)
 * @param {string} tipoDocumento - Tipo de documento
 * @returns {string} Tipo formateado
 */
export const formatearTipoDocumento = (tipoDocumento) => {
  if (!tipoDocumento) return 'Sin documento';
  
  const tipos = {
    'cedula': 'Cédula de Identidad',
    'rif': 'RIF',
    'pasaporte': 'Pasaporte',
    'identificacion_extranjera': 'Identificación Extranjera'
  };
  
  return tipos[tipoDocumento?.toLowerCase()] || tipoDocumento;
};

/**
 * Formatear nombre completo
 * @param {string} nombre - Nombre del cliente
 * @param {string} apellido - Apellido del cliente
 * @returns {string} Nombre completo
 */
export const formatearNombreCompleto = (nombre, apellido) => {
  if (!nombre && !apellido) return 'Sin nombre';
  const n = toTitleCase(nombre || '');
  const a = toTitleCase(apellido || '');
  return `${n} ${a}`.trim();
};

/**
 * Formatear información de contacto
 * @param {string} telefono - Teléfono del cliente
 * @param {string} email - Email del cliente
 * @returns {string} Información formateada
 */
export const formatearContacto = (telefono, email) => {
  const contactos = [];
  if (telefono) contactos.push(`📞 ${telefono}`);
  if (email) contactos.push(`✉️ ${email}`);
  return contactos.join(' | ') || 'Sin contacto';
};

/**
 * Validar formato de email
 * @param {string} email - Email a validar
 * @returns {boolean} Email válido
 */
export const validarEmail = (email) => {
  if (!email) return true; // Email opcional
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Validar formato de teléfono (Venezuela)
 * @param {string} telefono - Teléfono a validar
 * @returns {boolean} Teléfono válido
 */
export const validarTelefono = (telefono) => {
  if (!telefono) return true; // Teléfono opcional
  
  // Limpiar espacios, guiones, etc.
  const limpio = telefono.replace(/\s+/g, '').replace(/[\(\)\-]/g, '');
  
  // Patrón principal: 0412XXXXXXX, 0414XXXXXXX, 0416XXXXXXX, etc.
  const regex = /^(041[246]|042[46]|0412|0212|0241|0234|0235|0236|0237|0238|0239)\d{7}$/;
  
  return regex.test(limpio);
};

/**
 * Validar formato de documento según tipo (Venezuela)
 * @param {string} tipoDocumento - Tipo de documento
 * @param {string} documento - Número de documento
 * @returns {boolean} Documento válido
 */
export const validarDocumento = (tipoDocumento, documento) => {
  if (!tipoDocumento || !documento) return false;
  
  const documentoLimpio = documento.trim().toUpperCase();
  
  switch (tipoDocumento?.toLowerCase()) {
    case 'cedula':
      // Cédula venezolana: V-XXXXXXX
      return /^V-\d{7,8}$/.test(documentoLimpio);
    case 'rif':
      // RIF venezolano: J-XXXXXXXX-X
      return /^[JGVE]-\d{8}-\d$/.test(documentoLimpio);
    case 'pasaporte':
      // Pasaporte: al menos 6 caracteres
      return documentoLimpio.length >= 6;
    case 'identificacion_extranjera':
      // Identificación extranjera: al menos 4 caracteres
      return documentoLimpio.length >= 4;
    default:
      return documentoLimpio.length >= 4;
  }
};

/**
 * Formatear cédula venezolana automáticamente
 * @param {string} cedula - Número de cédula
 * @returns {string} Cédula formateada
 */
export const formatearCedulaVenezolana = (cedula) => {
  if (!cedula) return '';
  
  // Convertir a mayúsculas y remover caracteres no válidos
  let limpia = cedula.toUpperCase().replace(/[^V0-9]/g, '');
  
  // Si no empieza con V, agregarlo
  if (!limpia.startsWith('V')) {
    limpia = 'V' + limpia;
  }
  
  // Extraer solo los números (después de la V)
  const numeros = limpia.substring(1).replace(/\D/g, '');
  
  // Formato simple: V-XXXXXXX
  if (numeros.length === 0) {
    return 'V-';
  }
  return `V-${numeros}`;
};

/**
 * Formatear RIF venezolano automáticamente
 * @param {string} rif - Número de RIF
 * @returns {string} RIF formateado
 */
export const formatearRIFVenezolano = (rif) => {
  if (!rif) return '';
  
  // Convertir a mayúsculas y remover caracteres no válidos
  let limpia = rif.toUpperCase().replace(/[^JGVE0-9]/g, '');
  
  // Si no empieza con J, G, V o E, agregar J por defecto
  if (!/^[JGVE]/.test(limpia)) {
    limpia = 'J' + limpia;
  }
  
  // Extraer la letra y los números
  const letra = limpia.charAt(0);
  const numeros = limpia.substring(1).replace(/\D/g, '');
  
  // Formato: J-XXXXXXXX-X
  if (numeros.length === 0) {
    return `${letra}-`;
  } else if (numeros.length <= 8) {
    return `${letra}-${numeros}`;
  } else {
    return `${letra}-${numeros.slice(0, 8)}-${numeros.slice(8, 9)}`;
  }
};

/**
 * Formatear documento según tipo (Venezuela)
 * @param {string} tipoDocumento - Tipo de documento
 * @param {string} documento - Número de documento
 * @returns {string} Documento formateado
 */
export const formatearDocumento = (tipoDocumento, documento) => {
  if (!documento) return '';
  
  const doc = documento.toString().trim().toUpperCase();
  
  switch (tipoDocumento?.toLowerCase()) {
    case 'cedula':
      return formatearCedulaVenezolana(doc);
    case 'rif':
      return formatearRIFVenezolano(doc);
    default:
      return doc;
  }
};

/**
 * Formatear teléfono venezolano automáticamente
 * @param {string} telefono - Número de teléfono
 * @returns {string} Teléfono formateado
 */
export const formatearTelefonoVenezolano = (telefono) => {
  if (!telefono) return '';
  
  // Remover caracteres no válidos
  let limpia = telefono.replace(/\D/g, '');
  
  // Si empieza con 0, mantenerlo
  if (!limpia.startsWith('0')) {
    if (limpia.length === 10 && ['412', '414', '416', '424', '426'].some(prefijo => 
      limpia.startsWith(prefijo))) {
      limpia = '0' + limpia;
    }
  }
  
  // Formato simple: 0416-7470715
  if (limpia.length >= 4) {
    const prefijo = limpia.substring(0, 4);
    const numero = limpia.substring(4);
    return `${prefijo}-${numero}`;
  }
  
  return limpia;
};

/**
 * Obtener patrón de entrada para documento (Venezuela)
 * @param {string} tipoDocumento - Tipo de documento
 * @returns {string} Patrón de ejemplo
 */
export const getPatronDocumento = (tipoDocumento) => {
  switch (tipoDocumento?.toLowerCase()) {
    case 'cedula':
      return 'V-XXXXXXX (Ej: V-3175781)';
    case 'rif':
      return 'J-XXXXXXXX-X (Ej: J-12345678-9)';
    case 'pasaporte':
      return 'Mínimo 6 caracteres';
    case 'identificacion_extranjera':
      return 'Mínimo 4 caracteres';
    default:
      return '';
  }
};

/**
 * Obtener información del prefijo telefónico
 * @param {string} prefijo - Prefijo telefónico
 * @returns {Object} Información del prefijo
 */
export const obtenerInfoPrefijo = (prefijo) => {
  const info = {
    '0412': { operadora: 'Movistar', tipo: 'Móvil' },
    '0414': { operadora: 'Movistar', tipo: 'Móvil' },
    '0416': { operadora: 'Movistar', tipo: 'Móvil' },
    '0424': { operadora: 'Movistar', tipo: 'Móvil' },
    '0426': { operadora: 'Movistar', tipo: 'Móvil' },
    '0212': { operadora: 'CANTV', tipo: 'Fijo', ciudad: 'Caracas' },
    '0241': { operadora: 'CANTV', tipo: 'Fijo', ciudad: 'Valencia' }
  };
  
  return info[prefijo] || { operadora: 'Desconocida', tipo: 'Desconocido' };
};

/**
 * Buscar clientes por término
 * @param {Array} clientes - Lista de clientes
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Clientes filtrados
 */
export const buscarClientes = (clientes, termino) => {
  if (!termino || termino.trim() === '') {
    return clientes;
  }
  
  const terminoLower = termino.toLowerCase();
  return clientes.filter(cliente => 
    cliente.documento?.toLowerCase().includes(terminoLower) ||
    cliente.nombre?.toLowerCase().includes(terminoLower) ||
    cliente.apellido?.toLowerCase().includes(terminoLower) ||
    cliente.email?.toLowerCase().includes(terminoLower) ||
    cliente.telefono?.toLowerCase().includes(terminoLower) ||
    convertirEstadoATexto(cliente.estado)?.toLowerCase().includes(terminoLower)
  );
};

/**
 * Filtrar clientes por estado
 * @param {Array} clientes - Lista de clientes
 * @param {string} estado - 'all', 'activos', 'inactivos'
 * @returns {Array} Clientes filtrados
 */
export const filtrarClientesPorEstado = (clientes, estado) => {
  if (estado === 'all') return clientes;
  if (estado === 'activos') return clientes.filter(c => c.estado?.toLowerCase() === 'activo');
  if (estado === 'inactivos') return clientes.filter(c => c.estado?.toLowerCase() === 'inactivo');
  return clientes;
};

/**
 * Obtener estadísticas de clientes
 * @param {Array} clientes - Lista de clientes
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasClientes = (clientes) => {
  const activos = clientes.filter(c => c.estado?.toLowerCase() === 'activo').length;
  const inactivos = clientes.filter(c => c.estado?.toLowerCase() === 'inactivo').length;
  
  // Clientes por tipo de documento
  const porTipoDocumento = clientes.reduce((acc, cliente) => {
    const tipo = cliente.tipoDocumento || 'sin_tipo';
    acc[tipo] = (acc[tipo] || 0) + 1;
    return acc;
  }, {});
  
  // Clientes con email
  const conEmail = clientes.filter(c => c.email && c.email.trim() !== '').length;
  
  // Clientes con teléfono
  const conTelefono = clientes.filter(c => c.telefono && c.telefono.trim() !== '').length;
  
  // Clientes con dirección
  const conDireccion = clientes.filter(c => c.direccion && c.direccion.trim() !== '').length;
  
  // Clientes recientes (últimos 7 días)
  const sieteDiasAtras = new Date();
  sieteDiasAtras.setDate(sieteDiasAtras.getDate() - 7);
  const recientes = clientes.filter(c => {
    try {
      const fechaCreacion = new Date(c.fechaCreacion || 0);
      return fechaCreacion >= sieteDiasAtras;
    } catch {
      return false;
    }
  }).length;
  
  return {
    total: clientes.length,
    activos,
    inactivos,
    porcentajeActivos: clientes.length > 0 ? Math.round((activos / clientes.length) * 100) : 0,
    porcentajeInactivos: clientes.length > 0 ? Math.round((inactivos / clientes.length) * 100) : 0,
    porTipoDocumento,
    conEmail,
    conTelefono,
    conDireccion,
    porcentajeConEmail: clientes.length > 0 ? Math.round((conEmail / clientes.length) * 100) : 0,
    porcentajeConTelefono: clientes.length > 0 ? Math.round((conTelefono / clientes.length) * 100) : 0,
    porcentajeConDireccion: clientes.length > 0 ? Math.round((conDireccion / clientes.length) * 100) : 0,
    recientes,
    porcentajeRecientes: clientes.length > 0 ? Math.round((recientes / clientes.length) * 100) : 0
  };
};

/**
 * Ordenar clientes por diferentes criterios
 * @param {Array} clientes - Lista de clientes
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Clientes ordenados
 */
export const ordenarClientes = (clientes, criterio = 'nombre') => {
  const clientesCopy = [...clientes];
  
  switch (criterio) {
    case 'nombre':
      return clientesCopy.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    case 'nombre_desc':
      return clientesCopy.sort((a, b) => (b.nombre || '').localeCompare(a.nombre || ''));
    case 'apellido':
      return clientesCopy.sort((a, b) => (a.apellido || '').localeCompare(b.apellido || ''));
    case 'fechaCreacion_desc':
      return clientesCopy.sort((a, b) => new Date(b.fechaCreacion || 0) - new Date(a.fechaCreacion || 0));
    case 'fechaCreacion':
      return clientesCopy.sort((a, b) => new Date(a.fechaCreacion || 0) - new Date(b.fechaCreacion || 0));
    case 'estado':
      return clientesCopy.sort((a, b) => (a.estado || '').localeCompare(b.estado || ''));
    case 'documento':
      return clientesCopy.sort((a, b) => (a.documento || '').localeCompare(b.documento || ''));
    default:
      return clientesCopy;
  }
};

// ==================== FUNCIONES API CORREGIDAS ====================

/**
 * Obtener todos los clientes
 * @returns {Promise} Lista de clientes
 */
export const obtenerClientes = async () => {
  try {
    console.log('📋 Obteniendo clientes...');
    
    const response = await apiFetch('/Cliente');
    
    // La API devuelve directamente el array
    const clientes = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: clientes,
      count: clientes.length,
      message: 'Clientes obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo clientes:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Buscar clientes por término (usa el endpoint search)
 * @param {string} termino - Término de búsqueda
 * @returns {Promise} Clientes encontrados
 */
export const buscarClientesPorTermino = async (termino) => {
  try {
    if (!termino || termino.trim() === '') {
      return {
        success: true,
        data: [],
        count: 0,
        message: 'Ingrese un término de búsqueda'
      };
    }
    
    console.log(`🔍 Buscando clientes por: "${termino}"`);
    
    const response = await apiFetch('/Cliente/search', {
      method: 'POST',
      body: JSON.stringify({ termino: termino.trim() })
    });
    
    const clientes = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: clientes,
      count: clientes.length,
      message: 'Búsqueda completada'
    };
    
  } catch (error) {
    console.error('❌ Error buscando clientes:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener clientes activos
 * @returns {Promise} Lista de clientes activos
 */
export const obtenerClientesActivos = async () => {
  try {
    console.log('📋 Obteniendo clientes activos...');
    
    const response = await apiFetch('/Cliente/activos');
    
    const clientes = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: clientes,
      count: clientes.length,
      message: 'Clientes activos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo clientes activos:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener clientes por estado específico
 * @param {string} estado - Estado a filtrar ('activo', 'inactivo')
 * @returns {Promise} Lista de clientes filtrados
 */
export const obtenerClientesPorEstado = async (estado) => {
  try {
    if (!estado) {
      throw new Error('Estado es requerido');
    }
    
    console.log(`📋 Obteniendo clientes con estado: ${estado}`);
    
    const response = await apiFetch(`/Cliente/estado/${estado}`);
    
    const clientes = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: clientes,
      count: clientes.length,
      message: `Clientes con estado ${estado} obtenidos exitosamente`
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo clientes por estado ${estado}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener un cliente por ID
 * @param {number} id - ID del cliente
 * @returns {Promise} Datos del cliente
 */
export const obtenerClientePorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de cliente es requerido');
    }
    
    console.log(`🔍 Obteniendo cliente ID: ${id}`);
    
    const response = await apiFetch(`/Cliente/${id}`);
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Cliente obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo cliente ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener cliente con relaciones
 * @param {number} id - ID del cliente
 * @returns {Promise} Cliente con relaciones
 */
export const obtenerClienteConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de cliente es requerido');
    }
    
    console.log(`🔍 Obteniendo cliente ID: ${id} con relaciones`);
    
    const response = await apiFetch(`/Cliente/${id}/conRelaciones`);
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Cliente con relaciones obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo cliente ${id} con relaciones:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Verificar si un cliente existe por documento
 * @param {string} documento - Documento a verificar
 * @returns {Promise} Resultado de la verificación
 */
export const verificarClientePorDocumento = async (documento) => {
  try {
    if (!documento || documento.trim() === '') {
      throw new Error('Documento es requerido');
    }
    
    console.log(`🔎 Verificando cliente por documento: "${documento}"`);
    
    const response = await apiFetch(`/Cliente/documento/${documento}/exists`);
    
    return {
      success: true,
      existe: response.exists || false,
      message: response.exists ? 'El documento ya está registrado' : 'Documento disponible'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando cliente por documento "${documento}":`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Verificar si un cliente existe por ID
 * @param {number} id - ID a verificar
 * @returns {Promise} Resultado de la verificación
 */
export const verificarClientePorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID es requerido');
    }
    
    console.log(`🔎 Verificando cliente por ID: ${id}`);
    
    const response = await apiFetch(`/Cliente/${id}/exists`);
    
    return {
      success: true,
      existe: response.exists || false,
      message: response.exists ? 'El ID existe' : 'ID disponible'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando cliente por ID ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Crear un nuevo cliente
 * @param {Object} clienteData - Datos del cliente
 * @returns {Promise} Cliente creado
 */
export const crearCliente = async (clienteData) => {
  try {
    // Validar datos requeridos
    if (!clienteData.tipoDocumento) {
      throw new Error('El tipo de documento es requerido');
    }
    
    if (!clienteData.documento || clienteData.documento.trim() === '') {
      throw new Error('El documento es requerido');
    }
    
    if (!clienteData.nombre || clienteData.nombre.trim() === '') {
      throw new Error('El nombre del cliente es requerido');
    }
    
    console.log('➕ Creando nuevo cliente:', clienteData);
    
    // Convertir a formato API (snake_case)
    const apiData = convertirParaAPI(clienteData);
    
    const response = await apiFetch('/Cliente', {
      method: 'POST',
      body: JSON.stringify(apiData)
    });
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Cliente creado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando cliente:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar un cliente existente
 * @param {number} id - ID del cliente
 * @param {Object} clienteData - Datos a actualizar
 * @returns {Promise} Cliente actualizado
 */
export const actualizarCliente = async (id, clienteData) => {
  try {
    if (!id) {
      throw new Error('ID de cliente es requerido');
    }
    
    if (!clienteData.tipoDocumento) {
      throw new Error('El tipo de documento es requerido');
    }
    
    if (!clienteData.documento || clienteData.documento.trim() === '') {
      throw new Error('El documento es requerido');
    }
    
    if (!clienteData.nombre || clienteData.nombre.trim() === '') {
      throw new Error('El nombre del cliente es requerido');
    }
    
    console.log(`✏️ Actualizando cliente ID: ${id}`, clienteData);
    
    // Convertir a formato API (snake_case)
    const apiData = convertirParaAPI(clienteData);
    
    const response = await apiFetch(`/Cliente/${id}`, {
      method: 'PUT',
      body: JSON.stringify(apiData)
    });
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Cliente actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando cliente ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar un cliente
 * @param {number} id - ID del cliente a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarCliente = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de cliente es requerido');
    }
    
    console.log(`🗑️ Eliminando cliente ID: ${id}`);
    
    const response = await apiFetch(`/Cliente/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response,
      message: response.message || 'Cliente eliminado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando cliente ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cambiar estado de un cliente (usando endpoint de estado)
 * @param {number} id - ID del cliente
 * @param {string} estado - Nuevo estado
 * @returns {Promise} Resultado del cambio de estado
 */
export const cambiarEstadoCliente = async (id, estado) => {
  try {
    if (!id || !estado) {
      throw new Error('ID y estado son requeridos');
    }
    
    console.log(`🔄 Cambiando estado de cliente ID: ${id} a ${estado}`);
    
    // Primero obtenemos el cliente
    const clienteResult = await obtenerClientePorId(id);
    
    if (!clienteResult.success || !clienteResult.data) {
      throw new Error('No se pudo obtener el cliente');
    }
    
    // Actualizamos el estado
    const cliente = clienteResult.data;
    cliente.estado = estado;
    
    // Usamos PUT para actualizar
    return await actualizarCliente(id, cliente);
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de cliente ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Activar un cliente
 * @param {number} id - ID del cliente
 * @returns {Promise} Resultado de la activación
 */
export const activarCliente = async (id) => {
  return await cambiarEstadoCliente(id, 'activo');
};

/**
 * Desactivar un cliente
 * @param {number} id - ID del cliente
 * @returns {Promise} Resultado de la desactivación
 */
export const desactivarCliente = async (id) => {
  return await cambiarEstadoCliente(id, 'inactivo');
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Función de exportación a PDF
 * @param {Array} clientes - Lista de clientes
 * @param {string} titulo - Título del reporte
 * @param {string} logoBase64 - Logo en base64 (OPCIONAL)
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDF = (clientes, titulo = 'Reporte de Clientes', logoBase64 = null) => {
  try {
    console.log('📊 Exportando PDF...');
    console.log('📈 Clientes a exportar:', clientes.length);
    
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!clientes || clientes.length === 0) {
      throw new Error('No hay clientes para exportar');
    }
    
    // Usar jsPDF
    let jsPDF;
    try {
      const jspdf = require('jspdf');
      jsPDF = jspdf.jsPDF;
    } catch (e) {
      console.error('❌ jsPDF no está disponible:', e);
      return {
        success: false,
        error: 'La librería jsPDF no está disponible.',
        filename: null
      };
    }
    
    const doc = new jsPDF();
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const horaStr = fecha.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    let y = 20;
    const marginLeft = 20;
    
    // Agregar logo si se proporcionó
    let logoAgregado = false;
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', marginLeft, 10, 30, 15);
        console.log('✅ Logo agregado al PDF');
        logoAgregado = true;
        y = 35;
      } catch (logoError) {
        console.warn('⚠️ Error al agregar logo:', logoError);
        y = 20;
      }
    }
    
    // Título
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const titleX = logoAgregado ? 60 : marginLeft;
    doc.text('SYSTEM JJ', titleX, y);
    
    // Subtítulo
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(titulo, titleX, y + 8);
    
    // Información de fecha y total
    y += 20;
    doc.setFontSize(10);
    doc.text(`Fecha: ${fechaStr}`, marginLeft, y);
    doc.text(`Hora: ${horaStr}`, marginLeft + 50, y);
    doc.text(`Total: ${clientes.length} clientes`, marginLeft + 100, y);
    
    // Encabezados de tabla
    y += 15;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    
    const colPositions = {
      id: marginLeft,
      nombre: marginLeft + 15,
      documento: marginLeft + 60,
      telefono: marginLeft + 110,
      estado: marginLeft + 150
    };
    
    doc.text('ID', colPositions.id, y);
    doc.text('Nombre', colPositions.nombre, y);
    doc.text('Documento', colPositions.documento, y);
    doc.text('Teléfono', colPositions.telefono, y);
    doc.text('Estado', colPositions.estado, y);
    
    // Línea separadora
    y += 5;
    doc.setDrawColor(200, 200, 200);
    doc.line(marginLeft, y, marginLeft + 170, y);
    
    // Datos de los clientes
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    
    clientes.forEach((cliente, index) => {
      if (y > 270) {
        doc.addPage();
        y = 30;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('ID', colPositions.id, 20);
        doc.text('Nombre', colPositions.nombre, 20);
        doc.text('Documento', colPositions.documento, 20);
        doc.text('Teléfono', colPositions.telefono, 20);
        doc.text('Estado', colPositions.estado, 20);
        y += 15;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
      }
      
      const idStr = String(cliente.idCliente || index + 1);
      const nombreStr = String(formatearNombreCompleto(cliente.nombre, cliente.apellido) || 'N/A');
      const documentoStr = String(cliente.documento || 'N/A');
      const telefonoStr = String(cliente.telefono || 'N/A');
      const estado = convertirEstadoATexto(cliente.estado);
      const estadoStr = String(estado || 'N/A');
      
      // Truncar textos largos
      const truncarTexto = (texto, maxLength = 25) => {
        if (texto.length > maxLength) {
          return texto.substring(0, maxLength - 3) + '...';
        }
        return texto;
      };
      
      doc.text(truncarTexto(idStr, 10), colPositions.id, y);
      doc.text(truncarTexto(nombreStr, 20), colPositions.nombre, y);
      doc.text(truncarTexto(documentoStr, 20), colPositions.documento, y);
      doc.text(truncarTexto(telefonoStr, 15), colPositions.telefono, y);
      
      // Color según estado
      const estadoLower = estado.toLowerCase();
      if (estadoLower === 'activo') {
        doc.setTextColor(0, 128, 0);
      } else if (estadoLower === 'inactivo') {
        doc.setTextColor(255, 0, 0);
      } else {
        doc.setTextColor(128, 128, 128);
      }
      
      doc.text(truncarTexto(estadoStr, 10), colPositions.estado, y);
      doc.setTextColor(0, 0, 0);
      
      y += 6;
    });
    
    // Pie de página
    const pageWidth = doc.internal.pageSize.width;
    const pageCount = doc.internal.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text('© SYSTEM JJ - Sistema de Gestión de Clientes', marginLeft, 290);
      doc.text(`Página ${i} de ${pageCount} - ${fechaStr}`, pageWidth - marginLeft, 290, null, null, 'right');
    }
    
    // Guardar archivo
    const fechaISO = fecha.toISOString().split('T')[0];
    const nombreArchivo = `clientes_${fechaISO}.pdf`;
    
    doc.save(nombreArchivo);
    
    console.log('✅ PDF generado exitosamente:', nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: logoAgregado 
        ? 'PDF generado exitosamente con logo' 
        : 'PDF generado exitosamente',
      count: clientes.length,
      withLogo: logoAgregado
    };
    
  } catch (error) {
    console.error('❌ Error exportando PDF:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar clientes a Excel
 * @param {Array} clientes - Lista de clientes
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcel = (clientes, filename = 'clientes') => {
  try {
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!clientes || clientes.length === 0) {
      throw new Error('No hay clientes para exportar');
    }
    
    const XLSX = require('xlsx');
    
    // Preparar datos
    const data = clientes.map((cliente, index) => ({
      '#': index + 1,
      'ID': cliente.idCliente || '',
      'Tipo Documento': formatearTipoDocumento(cliente.tipoDocumento),
      'Documento': cliente.documento || '',
      'Nombre': cliente.nombre || '',
      'Apellido': cliente.apellido || '',
      'Nombre Completo': formatearNombreCompleto(cliente.nombre, cliente.apellido),
      'Teléfono': cliente.telefono || '',
      'Email': cliente.email || '',
      'Dirección': cliente.direccion || '',
      'Estado': convertirEstadoATexto(cliente.estado),
      'Fecha Creación': formatearFechaCliente(cliente.fechaCreacion, true)
    }));
    
    // Crear hoja de cálculo
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Ajustar anchos de columna
    const wscols = [
      { wch: 5 },   // #
      { wch: 8 },   // ID
      { wch: 15 },  // Tipo Documento
      { wch: 20 },  // Documento
      { wch: 20 },  // Nombre
      { wch: 20 },  // Apellido
      { wch: 25 },  // Nombre Completo
      { wch: 15 },  // Teléfono
      { wch: 30 },  // Email
      { wch: 40 },  // Dirección
      { wch: 12 },  // Estado
      { wch: 20 }   // Fecha Creación
    ];
    ws['!cols'] = wscols;
    
    // Crear libro
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
    
    // Generar nombre de archivo
    const fecha = new Date();
    const fechaStr = fecha.toISOString().split('T')[0];
    const nombreArchivo = `${filename}_${fechaStr}.xlsx`;
    
    // Guardar
    XLSX.writeFile(wb, nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: 'Archivo Excel generado exitosamente',
      count: clientes.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar reporte completo
 * @param {Array} clientes - Lista de clientes
 * @param {string} baseFilename - Nombre base
 * @param {string} logoBase64 - Logo en base64 (opcional)
 * @returns {Promise} Resultado
 */
export const exportarReporteCompleto = async (clientes, baseFilename = 'reporte_completo_clientes', logoBase64 = null) => {
  try {
    const excelResult = exportarExcel(clientes, baseFilename);
    if (!excelResult.success) {
      throw new Error(`Error en Excel: ${excelResult.error}`);
    }
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const pdfResult = exportarPDF(clientes, 'Reporte de Clientes - SYSTEM JJ', logoBase64);
    
    return {
      success: true,
      excel: excelResult,
      pdf: pdfResult,
      message: 'Reporte completo generado exitosamente',
      count: clientes.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando reporte completo:', error);
    return {
      success: false,
      error: error.message,
      excel: null,
      pdf: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Constantes
  TIPOS_DOCUMENTO,
  PREFIJOS_TELEFONO,
  
  // Funciones API
  obtenerClientes,
  buscarClientesPorTermino,
  obtenerClientesActivos,
  obtenerClientesPorEstado,
  obtenerClientePorId,
  obtenerClienteConRelaciones,
  verificarClientePorDocumento,
  verificarClientePorId,
  crearCliente,
  actualizarCliente,
  eliminarCliente,
  activarCliente,
  desactivarCliente,
  cambiarEstadoCliente,
  
  // Funciones auxiliares
  formatearFechaCliente,
  convertirEstadoATexto,
  convertirEstadoABoolean,
  formatearTipoDocumento,
  formatearNombreCompleto,
  formatearContacto,
  validarEmail,
  validarTelefono,
  validarDocumento,
  formatearCedulaVenezolana,
  formatearRIFVenezolano,
  formatearDocumento,
  formatearTelefonoVenezolano,
  obtenerInfoPrefijo,
  getPatronDocumento,
  buscarClientes,
  filtrarClientesPorEstado,
  obtenerEstadisticasClientes,
  ordenarClientes,
  
  // Funciones de exportación
  exportarExcel,
  exportarPDF,
  exportarReporteCompleto,
  
  // Funciones de conversión (por si se necesitan)
  convertirDesdeAPI,
  convertirParaAPI
};